import React from "react";
import type { LucideProps } from "lucide-react";

interface CreateIconOptions {
  displayName: string;
  viewBox: string;
  d?: string;
  path?: React.ReactElement<any> | React.ReactElement<any>[];
}

interface IconProps extends React.SVGProps<SVGSVGElement> {
  size?: string | number;
}

function createIcon(options: CreateIconOptions) {
  const { displayName, viewBox, d, path } = options;

  const IconComponent = React.forwardRef<SVGSVGElement, IconProps>(
    ({ size = "1em", className, ...props }, ref) => {
      return (
        <svg
          ref={ref}
          viewBox={viewBox}
          width={size}
          height={size}
          fill="currentColor"
          stroke="currentColor"
          strokeWidth={0.1}
          className={className}
          {...props}
        >
          {d ? <path d={d} /> : path}
        </svg>
      );
    }
  );

  IconComponent.displayName = displayName;

  return IconComponent;
}

export const AudioWaveIcon = createIcon({
  displayName: "AudioWaveIcon",
  viewBox: "0 0 34 24",
  d: "M1.884 15.583c.573 0 1.041-.397 1.041-.884V9.303c0-.49-.468-.884-1.041-.884s-1.042.394-1.042.884v5.395c0 .489.469.885 1.042.885zM4.44 5.66V18.34c0 .505.468.917 1.042.917.573 0 1.041-.41 1.041-.917V5.66c0-.505-.468-.917-1.041-.917-.574 0-1.042.41-1.042.917zM8.164 1.041V22.96a1.041 1.041 0 002.083 0V1.04a1.042 1.042 0 00-2.083 0zM12.014 4.823v14.354c0 .573.437 1.038 1.01 1.038s1.01-.464 1.01-1.038V4.823c0-.573-.437-1.038-1.01-1.038s-1.01.465-1.01 1.038zM15.801 9.635v4.73c0 .452.469.82 1.042.82.573 0 1.041-.367 1.041-.82v-4.73c0-.452-.468-.82-1.041-.82s-1.042.368-1.042.82zM19.715 5.579v12.842c0 .513.468.928 1.041.928s1.042-.416 1.042-.928V5.579c0-.513-.469-.928-1.042-.928-.573 0-1.041.415-1.041.928zM23.502 1.041v21.917c0 .574.437 1.039 1.01 1.039s1.01-.465 1.01-1.039V1.041c0-.573-.437-1.038-1.01-1.038s-1.01.465-1.01 1.038zM27.289 4.823v14.354a1.041 1.041 0 102.083 0V4.823a1.041 1.041 0 10-2.083 0zM32.117 8.376c-.573 0-1.041.418-1.041.933v5.382c0 .515.468.932 1.041.932s1.042-.417 1.042-.932V9.309c0-.515-.469-.933-1.042-.933z",
});

export const ChevronRightIcon = createIcon({
  displayName: "ChevronRightIcon",
  viewBox: "0 0 32 33",
  d: "M20.858 16.76c0 .47-.208.939-.556 1.274l-6.674 6.374c-.487.47-1.252.47-1.738 0a1.146 1.146 0 010-1.678l6.048-5.836a.199.199 0 000-.268l-6.048-5.837a1.146 1.146 0 010-1.677 1.248 1.248 0 011.738 0l6.605 6.373c.417.336.625.805.625 1.275z",
});

export const CloseIcon = createIcon({
  displayName: "CloseIcon",
  viewBox: "0 0 13 13",
  d: "M2.751 11.97l3.934-3.933 3.933 3.933a1.22 1.22 0 001.724 0 1.221 1.221 0 000-1.723L8.408 6.313l3.934-3.933a1.221 1.221 0 000-1.723 1.221 1.221 0 00-1.724 0L6.685 4.59 2.75.657a1.221 1.221 0 00-1.723 0 1.221 1.221 0 000 1.723L4.96 6.313l-3.933 3.934a1.221 1.221 0 000 1.723 1.22 1.22 0 001.723 0z",
});

export const DonutIcon1 = createIcon({
  displayName: "DonutIcon1",
  viewBox: "0 0 17 16",
  path: (
    <>
      <path
        fill="#EBEBF0"
        d="M16.4 8a8 8 0 11-16 0 8 8 0 0116 0zM3.2 8a5.2 5.2 0 1010.4 0A5.2 5.2 0 003.2 8z"
      />
      <path
        fill="#4CAF50"
        d="M9.92 15.854A8 8 0 108.26 16l.049-2.8a5.2 5.2 0 111.079-.094l.531 2.75z"
      />
    </>
  ),
});

export const DonutIcon2 = createIcon({
  displayName: "DonutIcon2",
  viewBox: "0 0 17 16",
  path: (
    <>
      <path
        fill="#EBEBF0"
        d="M16.4 8a8 8 0 11-16 0 8 8 0 0116 0zM3.2 8a5.2 5.2 0 1010.4 0A5.2 5.2 0 003.2 8z"
      />
      <path
        fill="#B4D455"
        d="M15.944 10.663A8 8 0 108.416 16l-.006-2.8a5.2 5.2 0 114.893-3.47l2.64.933z"
      />
    </>
  ),
});

export const DonutIcon3 = createIcon({
  displayName: "DonutIcon3",
  viewBox: "0 0 17 16",
  path: (
    <>
      <path
        fill="#EBEBF0"
        d="M16.4 8a8 8 0 11-16 0 8 8 0 0116 0zM3.2 8a5.2 5.2 0 1010.4 0A5.2 5.2 0 003.2 8z"
      />
      <path
        fill="#FFD700"
        d="M14.572 2.91A8 8 0 108.375 16l.008-2.8a5.2 5.2 0 114.03-8.508l2.16-1.781z"
      />
    </>
  ),
});

export const DonutIcon4 = createIcon({
  displayName: "DonutIcon4",
  viewBox: "0 0 16 16",
  path: (
    <>
      <path
        fill="#EBEBF0"
        d="M16 8A8 8 0 110 8a8 8 0 0116 0zM2.8 8a5.2 5.2 0 1010.4 0A5.2 5.2 0 002.8 8z"
      />
      <path
        fill="#FF8C4C"
        d="M5.31.466A8 8 0 007.98 16l.008-2.8A5.2 5.2 0 016.252 3.103L5.31.466z"
      />
    </>
  ),
});

export const DonutIcon5 = createIcon({
  displayName: "DonutIcon5",
  viewBox: "0 0 16 16",
  path: (
    <>
      <path
        fill="#EBEBF0"
        d="M16 8A8 8 0 110 8a8 8 0 0116 0zM2.8 8a5.2 5.2 0 1010.4 0A5.2 5.2 0 002.8 8z"
      />
      <path
        fill="#FF4C4C"
        d="M.19 6.265A8 8 0 007.985 16l.005-2.8a5.201 5.201 0 01-5.066-6.327L.19 6.265z"
      />
    </>
  ),
});

const EditIcon = createIcon({
  displayName: "EditIcon",
  viewBox: "0 0 16 16",
  d: "M13.273.004a.266.266 0 00-.133.075L1.408 11.812a.267.267 0 00-.059.1L.016 15.645a.267.267 0 00.342.341l3.733-1.333a.267.267 0 00.1-.066L15.923 2.854a.267.267 0 000-.375l-2.4-2.4a.267.267 0 00-.25-.075zm.059.642l2.016 2.025-1.216 1.216-2.025-2.016L13.332.646zm-1.6 1.6l2.016 2.025-9.74 9.74-2.026-2.024 9.75-9.741zM1.708 12.47l1.808 1.808-2.808 1.008 1-2.816z",
});

export const FileIcon = createIcon({
  displayName: "FileIcon",
  viewBox: "0 0 20 24",
  path: (
    <>
      <path
        fill="currentColor"
        d="M5.2 11.4h9.6a.6.6 0 100-1.2H5.2a.6.6 0 100 1.2zM5.2 15.599h9.6a.6.6 0 100-1.2H5.2a.6.6 0 100 1.2zM5.2 19.798h9.6a.6.6 0 100-1.2H5.2a.6.6 0 100 1.2z"
      />
      <path
        fill="currentColor"
        d="M3.4 23.999h13.2c1.654 0 3-1.346 3-3V7.593a2.98 2.98 0 00-.878-2.12L14.129.879A2.981 2.981 0 0012.007 0H3.401c-1.654 0-3 1.345-3 3v18c0 1.653 1.345 3 3 3v-.001zm14.153-18h-2.152c-.992 0-1.8-.807-1.8-1.8V2.048L17.553 6zM1.601 3c0-.992.808-1.8 1.8-1.8h8.606c.134 0 .265.018.394.047V4.2c0 1.653 1.345 3 3 3h2.952c.03.13.048.26.048.394v13.405c0 .992-.808 1.8-1.8 1.8h-13.2c-.992 0-1.8-.808-1.8-1.8v-18z"
      />
    </>
  ),
});

export const ImageIcon3 = createIcon({
  displayName: "ImageIcon3",
  viewBox: "0 0 30 24",
  path: (
    <>
      <path
        fill="currentColor"
        d="M26.25.001H3.752a3.755 3.755 0 00-3.75 3.75V20.25A3.755 3.755 0 003.751 24H26.25A3.755 3.755 0 0030 20.25V3.75A3.755 3.755 0 0026.25 0v.001zm-22.498 1.5H26.25c1.24 0 2.25 1.009 2.25 2.25v8.786l-3.475-3.474A3.733 3.733 0 0022.37 7.96h-.007c-1 .002-1.94.394-2.642 1.103l-6.722 6.72-2.221-2.22A3.733 3.733 0 008.12 12.46h-.008c-1 .001-1.939.394-2.642 1.102l-3.969 3.97V3.75c0-1.24 1.01-2.25 2.25-2.25zM1.502 20.25v-.598l5.031-5.031a2.23 2.23 0 011.583-.661h.005c.6 0 1.167.235 1.594.662l7.878 7.878H3.752c-1.24 0-2.25-1.01-2.25-2.25zM26.25 22.5h-6.537l-5.654-5.654 6.723-6.723a2.23 2.23 0 011.583-.661h.004c.6 0 1.167.235 1.595.662l4.535 4.535v5.592c0 1.24-1.01 2.25-2.25 2.25V22.5z"
      />
      <path
        fill="currentColor"
        d="M7.502 9.75c1.655 0 2.999-1.345 2.999-2.999 0-1.655-1.346-3-3-3-1.654.001-2.999 1.347-2.999 3 0 1.655 1.346 3 3 3zm0-4.5a1.502 1.502 0 010 3c-.827.002-1.5-.67-1.5-1.499 0-.827.671-1.5 1.5-1.5z"
      />
    </>
  ),
});

export const LinkIcon = createIcon({
  displayName: "LinkIcon",
  viewBox: "0 0 25 24",
  path: (
    <>
      <path
        fill="currentColor"
        d="M5.214 24h13.072a4.37 4.37 0 004.357-4.357v-7.988c0-.4-.327-.726-.727-.726-.399 0-.726.327-.726.726v7.988a2.913 2.913 0 01-2.905 2.905H5.215a2.913 2.913 0 01-2.905-2.905V6.572a2.913 2.913 0 012.904-2.905h7.988c.4 0 .726-.327.726-.726 0-.4-.326-.726-.726-.726H5.214A4.37 4.37 0 00.857 6.572v13.071A4.37 4.37 0 005.214 24z"
      />
      <path
        fill="currentColor"
        d="M24.966 7.334V.508c0-.036 0-.072-.036-.109l-.037-.036c0-.036-.036-.073-.036-.073a.616.616 0 00-.218-.217c-.036-.037-.072-.037-.109-.037L24.421 0h-6.826c-.4 0-.726.327-.726.726 0 .4.327.726.726.726h4.902L11.858 12.018a.702.702 0 000 1.017c.29.29.727.29 1.017 0L23.514 2.433v4.901c0 .4.326.726.726.726.4 0 .726-.326.726-.726z"
      />
    </>
  ),
});

export const TextIcon = createIcon({
  displayName: "TextIcon",
  viewBox: "0 0 28 24",
  d: "M2 2.667h24c.735 0 1.334-.599 1.334-1.334C27.334.598 26.735 0 26 0H2C1.266 0 .667.598.667 1.333s.599 1.334 1.334 1.334zM19.334 21.333H2c-.735 0-1.334.598-1.334 1.333S1.266 24 2.001 24h17.333c.735 0 1.333-.599 1.333-1.334 0-.735-.598-1.333-1.333-1.333zM2 13.334h8c.736 0 1.334-.599 1.334-1.334 0-.735-.598-1.333-1.333-1.333H2c-.735 0-1.334.598-1.334 1.333s.599 1.334 1.334 1.334zM26 10.667h-6.666c-.735 0-1.334.598-1.334 1.333s.599 1.334 1.334 1.334H26c.735 0 1.334-.599 1.334-1.334 0-.735-.599-1.333-1.334-1.333zM2 8h2.667c.735 0 1.334-.599 1.334-1.334 0-.735-.599-1.333-1.334-1.333H2.001c-.735 0-1.334.598-1.334 1.333S1.266 8 2.001 8zM26 5.333h-9.333c-.735 0-1.333.598-1.333 1.333S15.932 8 16.667 8h9.334c.735 0 1.333-.599 1.333-1.334 0-.735-.598-1.333-1.333-1.333zM11.334 5.333H10c-.735 0-1.334.598-1.334 1.333S9.266 8 10.001 8h1.333c.735 0 1.333-.599 1.333-1.334 0-.735-.598-1.333-1.333-1.333zM15.334 10.667H14c-.735 0-1.334.598-1.334 1.333s.599 1.334 1.334 1.334h1.333c.735 0 1.333-.599 1.333-1.334 0-.735-.598-1.333-1.333-1.333zM2 18.667h2.667c.735 0 1.334-.599 1.334-1.334C6 16.598 5.402 16 4.667 16H2.001c-.735 0-1.334.598-1.334 1.333s.599 1.334 1.334 1.334zM11.334 16H10c-.735 0-1.334.598-1.334 1.333s.599 1.334 1.334 1.334h1.333c.735 0 1.333-.599 1.333-1.334 0-.735-.598-1.333-1.333-1.333zM15.334 17.333c0 .735.598 1.334 1.333 1.334h1.334c.735 0 1.333-.599 1.333-1.334 0-.735-.598-1.333-1.333-1.333h-1.334c-.735 0-1.333.598-1.333 1.333zM26 16h-2.666c-.735 0-1.334.598-1.334 1.333s.599 1.334 1.334 1.334H26c.735 0 1.334-.599 1.334-1.334 0-.735-.599-1.333-1.334-1.333zM26 21.333h-2.666c-.735 0-1.334.598-1.334 1.333S22.6 24 23.334 24H26c.735 0 1.334-.599 1.334-1.334 0-.735-.599-1.333-1.334-1.333z",
});

// The Lucide-style AI search mark (prop-driven like lucide-react icons).
export const AISearchIcon = ({
  color = "currentColor",
  size = 24,
  strokeWidth = 1,
  ...props
}: LucideProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path
      d="M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M21.0002 20.9992L16.7002 16.6992"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M14.7773 7.72497C14.7461 7.60385 14.683 7.49331 14.5945 7.40486C14.5061 7.31641 14.3955 7.25328 14.2744 7.22203L12.1272 6.66835C12.0906 6.65795 12.0583 6.63588 12.0354 6.6055C12.0124 6.57512 12 6.53808 12 6.5C12 6.46192 12.0124 6.42488 12.0354 6.3945C12.0583 6.36412 12.0906 6.34205 12.1272 6.33165L14.2744 5.77762C14.3955 5.7464 14.506 5.68332 14.5944 5.59494C14.6829 5.50655 14.746 5.39609 14.7773 5.27503L15.331 3.12783C15.3413 3.09106 15.3634 3.05866 15.3938 3.03557C15.4242 3.01249 15.4614 3 15.4995 3C15.5377 3 15.5749 3.01249 15.6053 3.03557C15.6357 3.05866 15.6578 3.09106 15.6681 3.12783L16.2214 5.27503C16.2527 5.39615 16.3158 5.50669 16.4042 5.59514C16.4927 5.68359 16.6032 5.74672 16.7243 5.77797L18.8715 6.3313C18.9085 6.34149 18.941 6.36351 18.9642 6.39398C18.9874 6.42445 19 6.4617 19 6.5C19 6.5383 18.9874 6.57555 18.9642 6.60602C18.941 6.63649 18.9085 6.65851 18.8715 6.6687L16.7243 7.22203C16.6032 7.25328 16.4927 7.31641 16.4042 7.40486C16.3158 7.49331 16.2527 7.60385 16.2214 7.72497L15.6677 9.87217C15.6574 9.90894 15.6354 9.94134 15.605 9.96443C15.5745 9.98751 15.5374 10 15.4992 10C15.461 10 15.4239 9.98751 15.3934 9.96443C15.363 9.94134 15.341 9.90894 15.3307 9.87217L14.7773 7.72497Z"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

export const LikeIcon = createIcon({
  displayName: "LikeIcon",
  viewBox: "0 0 16 16",
  path: (
    <>
      <path
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.25"
        d="M4.664 6.166a.5.5 0 01.5.5v8a.5.5 0 11-1 0v-8a.5.5 0 01.5-.5z"
      />
      <path
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.25"
        d="M7.556 1.11a.5.5 0 01.453-.276 2.587 2.587 0 012.48 3.205l-.001.002-.516 2.126h3.25a1.833 1.833 0 011.76 2.347l-1.553 5.333a1.834 1.834 0 01-1.76 1.32h-9a1.833 1.833 0 01-1.833-1.833V8.001a1.833 1.833 0 011.833-1.834h1.84a.833.833 0 00.746-.462v-.002l2.3-4.593zm.74.756L6.15 6.15a1.837 1.837 0 01-1.64 1.017H2.67a.833.833 0 00-.834.834v5.333a.833.833 0 00.833.833h9a.834.834 0 00.8-.6l1.554-5.333a.835.835 0 00-.8-1.067H9.336a.5.5 0 01-.486-.618l.667-2.746V3.8a1.587 1.587 0 00-1.222-1.935z"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="0.25"
        d="M5 7l3.5-5.5L10 3 9 7h5.5l-2 7.5H5V7z"
      />
    </>
  ),
});

export const LikeFilledIcon = createIcon({
  displayName: "LikeFilledIcon",
  viewBox: "0 0 16 16",
  path: (
    <>
      <path
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.25"
        d="M4.664 6.166a.5.5 0 01.5.5v8a.5.5 0 11-1 0v-8a.5.5 0 01.5-.5z"
      />
      <path
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.25"
        d="M7.556 1.11a.5.5 0 01.453-.276 2.587 2.587 0 012.48 3.205l-.001.002-.516 2.126h3.25a1.833 1.833 0 011.76 2.347l-1.553 5.333a1.834 1.834 0 01-1.76 1.32h-9a1.833 1.833 0 01-1.833-1.833V8.001a1.833 1.833 0 011.833-1.834h1.84a.833.833 0 00.746-.462v-.002l2.3-4.593zm.74.756L6.15 6.15a1.837 1.837 0 01-1.64 1.017H2.67a.833.833 0 00-.834.834v5.333a.833.833 0 00.833.833h9a.834.834 0 00.8-.6l1.554-5.333a.835.835 0 00-.8-1.067H9.336a.5.5 0 01-.486-.618l.667-2.746V3.8a1.587 1.587 0 00-1.222-1.935z"
      />
      <path
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.25"
        d="M5 7l3.5-5.5L10 3 9 7h5.5l-2 7.5H5V7z"
      />
    </>
  ),
});

export const IdIcon = createIcon({
  displayName: "IdIcon",
  viewBox: "0 0 18 18",
  path: (
    <>
      <rect x="1" y="1" width="16" height="16" rx="4" fill="#F3F4F6" />
      <text
        x="50%"
        y="55%"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="9"
        fontWeight="bold"
        fill="#1E293B"
        style={{ fontFamily: "monospace" }}
      >
        ID
      </text>
    </>
  ),
});

export const TriangleRightIcon = createIcon({
  displayName: "TriangleRightIcon",
  viewBox: "0 0 8 8",
  path: (
    <path
      d="M3 2.5
         Q2.8 2.5 2.8 2.7
         L2.8 5.3
         Q2.8 5.5 3 5.5
         Q3.1 5.5 3.2 5.4
         L5 4.1
         Q5.2 4 5 3.9
         L3.2 2.6
         Q3.1 2.5 3 2.5Z"
      fill="currentColor"
    />
  ),
});

export const GmailIcon = (props: IconProps) => {
  const { size, className, ...rest } = props;
  const width = size || 27;
  const height = (Number(width) * 18) / 27 || 18;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 27 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...rest}
    >
      <g clipPath="url(#clip0_gmail_exact)">
        <rect width="27" height="18" fill="white" />
        <path
          d="M1.84091 18.0004H6.13636V8.72763L0 4.63672V16.364C0 17.2694 0.825341 18.0004 1.84091 18.0004Z"
          fill="#4285F4"
        />
        <path
          d="M20.8636 18.0004H25.1591C26.1777 18.0004 27 17.2667 27 16.364V4.63672L20.8636 8.72763"
          fill="#34A853"
        />
        <path
          d="M20.8636 1.6371V8.72801L27 4.6371V2.45528C27 0.431645 24.4013 -0.721991 22.5818 0.491645"
          fill="#FBBC04"
        />
        <path
          d="M6.13635 8.72763V1.63672L13.5 6.54581L20.8636 1.63672V8.72763L13.5 13.6367"
          fill="#EA4335"
        />
        <path
          d="M0 2.45528V4.6371L6.13636 8.72801V1.6371L4.41818 0.491645C2.59568 -0.721991 0 0.431645 0 2.45528Z"
          fill="#C5221F"
        />
      </g>
      <defs>
        <clipPath id="clip0_gmail_exact">
          <rect width="27" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const OutlookIcon = (props: IconProps) => {
  const { size, className, ...rest } = props;
  const width = size || 30;
  const height = (Number(width) * 27) / 30 || 27;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 30 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...rest}
    >
      <g clipPath="url(#clip0_outlook_exact)">
        <path
          d="M30 14.1735C30.0016 13.9465 29.8804 13.7353 29.6805 13.6166H29.677L29.6644 13.6099L19.2691 7.65636C19.2242 7.62702 19.1776 7.6002 19.1295 7.57604C18.7282 7.3757 18.2514 7.3757 17.85 7.57604C17.8019 7.60021 17.7553 7.62702 17.7104 7.65636L7.31513 13.6099L7.30258 13.6166C6.98487 13.8078 6.88747 14.2119 7.08504 14.5193C7.14325 14.6099 7.22343 14.6853 7.31862 14.7391L17.714 20.6926C17.759 20.7217 17.8056 20.7485 17.8535 20.773C18.2549 20.9733 18.7317 20.9733 19.133 20.773C19.181 20.7485 19.2275 20.7217 19.2726 20.6926L29.6679 14.7391C29.8756 14.622 30.0024 14.4061 30 14.1735Z"
          fill="#0A2767"
        />
        <path
          d="M8.52693 10.1999H15.3488V16.2499H8.52693V10.1999ZM28.6046 4.05V1.28249C28.621 0.590554 28.0549 0.0165804 27.3397 0H9.63694C8.92176 0.0165804 8.35567 0.590554 8.37205 1.28249V4.05L18.8372 6.74999L28.6046 4.05Z"
          fill="#0364B8"
        />
        <path
          d="M8.37207 4.05078H15.3488V10.1258H8.37207V4.05078Z"
          fill="#0078D4"
        />
        <path
          d="M22.3256 4.05078H15.3489V10.1258L22.3256 16.2008H28.6047V10.1258L22.3256 4.05078Z"
          fill="#28A8EA"
        />
        <path d="M15.3489 10.125H22.3256V16.2H15.3489V10.125Z" fill="#0078D4" />
        <path
          d="M15.3489 16.1992H22.3256V22.2742H15.3489V16.1992Z"
          fill="#0364B8"
        />
        <path
          d="M8.52698 16.25H15.3488V21.7499H8.52698V16.25Z"
          fill="#14447D"
        />
        <path
          d="M22.3256 16.1992H28.6046V22.2742H22.3256V16.1992Z"
          fill="#0078D4"
        />
        <path
          d="M29.6804 14.7043L29.6671 14.7111L19.2718 20.3676C19.2264 20.3945 19.1804 20.4202 19.1322 20.4431C18.9557 20.5245 18.7639 20.5704 18.5685 20.5782L18.0006 20.2568C17.9526 20.2335 17.906 20.2076 17.8611 20.1792L7.32621 14.3621H7.32133L6.97668 14.1758V25.6265C6.98206 26.3904 7.62635 27.0056 8.41598 27.0008H28.5829C28.5948 27.0008 28.6053 26.9954 28.6178 26.9954C28.7846 26.9851 28.949 26.9519 29.1062 26.8968C29.1741 26.869 29.2397 26.836 29.3022 26.7983C29.349 26.7726 29.4292 26.7166 29.4292 26.7166C29.7866 26.4607 29.9982 26.0566 29.9999 25.6265V14.1758C29.9997 14.3948 29.8776 14.5967 29.6804 14.7043Z"
          fill="url(#paint0_linear_outlook_exact)"
        />
        <path
          opacity="0.5"
          d="M29.4418 14.1304V14.8324L18.5721 22.0731L7.31858 14.3666C7.31858 14.3629 7.31545 14.3599 7.3116 14.3599L6.27905 13.7591V13.2528L6.70464 13.2461L7.60463 13.7456L7.62555 13.7524L7.70229 13.7996C7.70229 13.7996 18.279 19.6384 18.3069 19.6518L18.7116 19.8813C18.7465 19.8678 18.7813 19.8543 18.8232 19.8408C18.8441 19.8273 29.3232 14.1236 29.3232 14.1236L29.4418 14.1304Z"
          fill="#0A2767"
        />
        <path
          d="M29.6805 14.7043L29.6672 14.7117L19.2719 20.3682C19.2265 20.3952 19.1805 20.4209 19.1324 20.4438C18.7286 20.6346 18.2565 20.6346 17.8528 20.4438C17.805 20.4209 17.7584 20.3957 17.7133 20.3682L7.31797 14.7117L7.30541 14.7043C7.10471 14.599 6.97889 14.3967 6.97681 14.1758V25.6265C6.9818 26.3903 7.62586 27.0056 8.41535 27.0008C8.41535 27.0008 8.41538 27.0008 8.41541 27.0008H28.5614C29.3509 27.0056 29.995 26.3904 30 25.6265C30 25.6265 30 25.6265 30 25.6265V14.1758C29.9998 14.3948 29.8777 14.5967 29.6805 14.7043Z"
          fill="#1490DF"
        />
        <path
          opacity="0.1"
          d="M19.4232 20.2852L19.2676 20.3695C19.2225 20.3973 19.1759 20.4228 19.1281 20.4458C18.9567 20.5272 18.7703 20.5749 18.5797 20.5862L22.5348 25.1114L29.4341 26.7199C29.6232 26.5818 29.7736 26.4001 29.8716 26.1914L19.4232 20.2852Z"
          fill="#1E1E1E"
        />
        <path
          opacity="0.05"
          d="M20.1278 19.8984L19.2676 20.3662C19.2225 20.394 19.1759 20.4195 19.1281 20.4425C18.9567 20.5239 18.7703 20.5716 18.5797 20.5829L20.4327 25.5259L29.4362 26.7146C29.7909 26.4569 29.9997 26.0529 29.9999 25.6238V25.4759L20.1278 19.8984Z"
          fill="#1E1E1E"
        />
        <path
          d="M8.43494 27.0008H28.5593C28.869 27.0023 29.171 26.9077 29.421 26.7308L18 20.2582C17.9521 20.2349 17.9055 20.209 17.8605 20.1806L7.32563 14.3634H7.32075L6.97681 14.1758V25.5873C6.97602 26.3672 7.62885 27 8.43494 27.0008Z"
          fill="#28A8EA"
        />
        <path
          opacity="0.1"
          d="M16.7441 6.63571V21.0335C16.7429 21.5383 16.4256 21.992 15.9418 22.181C15.7919 22.2433 15.6305 22.2754 15.4674 22.2755H6.97668V6.07343H8.37204V5.39844H15.4674C16.1722 5.40102 16.7426 5.95379 16.7441 6.63571Z"
          fill="#1E1E1E"
        />
        <path
          opacity="0.2"
          d="M16.0465 7.31155V21.7093C16.0482 21.8723 16.0125 22.0337 15.9418 22.1818C15.748 22.6438 15.2851 22.947 14.7697 22.9492H6.97668V6.07428H14.7697C14.9721 6.07231 15.1716 6.12116 15.3488 6.21603C15.7764 6.42449 16.0463 6.84823 16.0465 7.31155Z"
          fill="#1E1E1E"
        />
        <path
          opacity="0.2"
          d="M16.0465 7.31155V20.3593C16.043 21.0409 15.4742 21.5934 14.7697 21.5993H6.97668V6.07428H14.7697C14.9721 6.07231 15.1716 6.12116 15.3488 6.21603C15.7765 6.42449 16.0463 6.84823 16.0465 7.31155Z"
          fill="#1E1E1E"
        />
        <path
          opacity="0.2"
          d="M15.3488 7.31149V20.3592C15.348 21.0419 14.7776 21.5959 14.072 21.5992H6.97668V6.07422H14.072C14.7775 6.07458 15.3491 6.62824 15.3488 7.31082C15.3488 7.31104 15.3488 7.31127 15.3488 7.31149Z"
          fill="#1E1E1E"
        />
        <path
          d="M1.27883 6.07422H14.07C14.7763 6.07422 15.3488 6.62817 15.3488 7.31149V19.6869C15.3488 20.3703 14.7763 20.9242 14.07 20.9242H1.27883C0.572546 20.9242 0 20.3702 0 19.6869V7.31149C0 6.62817 0.572562 6.07422 1.27883 6.07422Z"
          fill="url(#paint1_linear_outlook_exact)"
        />
        <path
          d="M3.997 11.2627C4.31219 10.613 4.82093 10.0693 5.45793 9.70144C6.16337 9.31069 6.96656 9.11589 7.7791 9.13848C8.53218 9.12268 9.27528 9.30738 9.92724 9.6724C10.5402 10.0261 11.0339 10.5443 11.3484 11.1641C11.6909 11.8472 11.8616 12.5989 11.8465 13.3579C11.8631 14.1511 11.6875 14.9371 11.3337 15.6529C11.0118 16.2949 10.5024 16.8316 9.86863 17.1966C9.19154 17.5728 8.42085 17.7624 7.64026 17.7447C6.8711 17.7627 6.11161 17.5759 5.44468 17.2047C4.82639 16.8506 4.32651 16.3318 4.00468 15.7103C3.66016 15.0371 3.48745 14.2939 3.50096 13.5428C3.48661 12.7563 3.65618 11.9769 3.997 11.2627ZM5.5542 14.9279C5.72225 15.3387 6.00723 15.6949 6.37606 15.9553C6.75174 16.2093 7.20159 16.3404 7.65978 16.3292C8.14773 16.3479 8.62871 16.2124 9.03001 15.9431C9.39417 15.6836 9.67166 15.3264 9.82815 14.9158C10.0031 14.4572 10.0894 13.9715 10.0828 13.4828C10.0882 12.9894 10.0071 12.4987 9.84279 12.0315C9.69772 11.6098 9.42917 11.2379 9.06978 10.961C8.67853 10.679 8.19885 10.536 7.7114 10.556C7.24329 10.5443 6.78336 10.6763 6.39769 10.9333C6.02262 11.1948 5.73223 11.5542 5.56048 11.9694C5.17948 12.9213 5.17749 13.9761 5.55489 14.9293L5.5542 14.9279Z"
          fill="white"
        />
        <path
          d="M22.3256 4.05078H28.6046V10.1258H22.3256V4.05078Z"
          fill="#50D9FF"
        />
      </g>
      <defs>
        <linearGradient
          id="paint0_linear_outlook_exact"
          x1="18.4883"
          y1="14.1758"
          x2="18.4883"
          y2="27.0008"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#35B8F1" />
          <stop offset="1" stopColor="#28A8EA" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_outlook_exact"
          x1="2.6664"
          y1="5.10744"
          x2="12.1943"
          y2="22.1637"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#1784D9" />
          <stop offset="0.5" stopColor="#107AD5" />
          <stop offset="1" stopColor="#0A63C9" />
        </linearGradient>
        <clipPath id="clip0_outlook_exact">
          <rect width="30" height="27" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};
